import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Theme = 'dark' | 'light' | 'auto';
export type ColorScheme = 'default' | 'blue' | 'green' | 'purple' | 'orange';

export interface DataDisplaySettings {
  [key: string]: {
    visible: boolean;
    order: number;
  };
}

export interface CustomizationSettings {
  theme: Theme;
  colorScheme: ColorScheme;
  dataDisplay: {
    [pageName: string]: DataDisplaySettings;
  };
}

interface CustomizationContextType {
  settings: CustomizationSettings;
  updateTheme: (theme: Theme) => void;
  updateColorScheme: (colorScheme: ColorScheme) => void;
  updateDataDisplay: (pageName: string, dataSettings: DataDisplaySettings) => void;
  isCustomizationOpen: boolean;
  setIsCustomizationOpen: (open: boolean) => void;
}

const defaultSettings: CustomizationSettings = {
  theme: 'dark',
  colorScheme: 'default',
  dataDisplay: {}
};

const CustomizationContext = createContext<CustomizationContextType | undefined>(undefined);

export function CustomizationProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<CustomizationSettings>(defaultSettings);
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);

  const updateTheme = (theme: Theme) => {
    setSettings(prev => ({ ...prev, theme }));
    // Apply theme to document
    document.documentElement.classList.remove('light', 'dark');
    if (theme !== 'auto') {
      document.documentElement.classList.add(theme);
    }
  };

  const updateColorScheme = (colorScheme: ColorScheme) => {
    setSettings(prev => ({ ...prev, colorScheme }));
    // Apply color scheme to document
    document.documentElement.setAttribute('data-color-scheme', colorScheme);
  };

  const updateDataDisplay = (pageName: string, dataSettings: DataDisplaySettings) => {
    setSettings(prev => ({
      ...prev,
      dataDisplay: {
        ...prev.dataDisplay,
        [pageName]: dataSettings
      }
    }));
  };

  return (
    <CustomizationContext.Provider value={{
      settings,
      updateTheme,
      updateColorScheme,
      updateDataDisplay,
      isCustomizationOpen,
      setIsCustomizationOpen
    }}>
      {children}
    </CustomizationContext.Provider>
  );
}

export function useCustomization() {
  const context = useContext(CustomizationContext);
  if (context === undefined) {
    throw new Error('useCustomization must be used within a CustomizationProvider');
  }
  return context;
}